import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Plus,
  Send,
  Eye,
  Trash2,
  Play,
  Pause,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft
} from 'lucide-react';

const WACampaignsPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/wa/campaigns');
      setCampaigns(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.delete(`/wa/campaigns/${campaignId}`);
      fetchCampaigns();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting campaign');
    }
  };

  const handlePause = async (campaignId) => {
    try {
      await api.post(`/wa/campaigns/${campaignId}/pause`);
      fetchCampaigns();
    } catch (error) {
    }
  };

  const handleResume = async (campaignId) => {
    try {
      await api.post(`/wa/campaigns/${campaignId}/resume`);
      fetchCampaigns();
    } catch (error) {
    }
  };

  const handleForceComplete = async (campaignId) => {
    if (!window.confirm('Mark this campaign as completed? All remaining unsent messages will be skipped.')) return;
    try {
      await api.post(`/wa/campaigns/${campaignId}/force-complete`);
      fetchCampaigns();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error completing campaign');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-100 text-gray-700', icon: '📝' },
      scheduled: { color: 'bg-blue-100 text-blue-700', icon: '📅' },
      ready: { color: 'bg-indigo-100 text-indigo-700', icon: '🔵' },
      running: { color: 'bg-green-100 text-green-700', icon: '🟢' },
      paused: { color: 'bg-yellow-100 text-yellow-700', icon: '⏸️' },
      completed: { color: 'bg-purple-100 text-purple-700', icon: '✅' }
    };
    return badges[status] || badges.draft;
  };

  const getProgressPercentage = (stats) => {
    if (!stats || stats.total === 0) return 0;
    return Math.round((stats.sent / stats.total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/whatsapp')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
            <p className="text-gray-500 mt-1">Manage your WhatsApp broadcast campaigns</p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/whatsapp/campaigns/new')}
          className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
        >
          <Plus size={16} className="mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Campaigns Table */}
      <Card className="bg-white border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Stats</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {campaigns.length > 0 ? campaigns.map((campaign) => {
                const statusInfo = getStatusBadge(campaign.status);
                const progress = getProgressPercentage(campaign.stats);
                
                // Format scheduled time - value is stored as UTC, append Z if missing
                const formatScheduledTime = (scheduledAt) => {
                  if (!scheduledAt) return null;
                  const utcStr = scheduledAt.endsWith('Z') ? scheduledAt : scheduledAt + 'Z';
                  const date = new Date(utcStr);
                  return date.toLocaleString('en-MY', { 
                    year: 'numeric',
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                    timeZone: 'Asia/Kuala_Lumpur'
                  });
                };
                
                return (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <p className="font-medium text-gray-900">{campaign.name}</p>
                      <p className="text-sm text-gray-500">by {campaign.created_by}</p>
                      {campaign.type === 'scheduled' && campaign.scheduled_at && (
                        <p className="text-xs text-blue-600 mt-1">
                          📅 Scheduled: {formatScheduledTime(campaign.scheduled_at)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`capitalize text-gray-600 ${campaign.type === 'scheduled' ? 'text-blue-600 font-medium' : ''}`}>
                        {campaign.type}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                        {statusInfo.icon} {campaign.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-[#25D366] h-2 rounded-full transition-all" 
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{progress}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={14} />
                          {campaign.stats?.sent || 0}
                        </span>
                        <span className="flex items-center gap-1 text-yellow-600">
                          <Clock size={14} />
                          {campaign.stats?.pending || 0}
                        </span>
                        <span className="flex items-center gap-1 text-red-600">
                          <XCircle size={14} />
                          {campaign.stats?.failed || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-500">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        {campaign.status === 'scheduled' ? (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/whatsapp/campaigns/${campaign.id}/queue`)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Eye size={14} className="mr-1" />
                              Preview
                            </Button>
                          </>
                        ) : campaign.status === 'running' ? (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/whatsapp/campaigns/${campaign.id}/queue`)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <Send size={14} className="mr-1" />
                              Continue
                            </Button>
                            <button 
                              onClick={() => handlePause(campaign.id)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Pause"
                            >
                              <Pause size={16} className="text-yellow-600" />
                            </button>
                            <button 
                              onClick={() => handleForceComplete(campaign.id)}
                              className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Mark as Completed"
                            >
                              <CheckCircle size={16} className="text-purple-600" />
                            </button>
                          </>
                        ) : campaign.status === 'paused' ? (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleResume(campaign.id)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <Play size={14} className="mr-1" />
                              Resume
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleForceComplete(campaign.id)}
                              className="text-purple-600 border-purple-200 hover:bg-purple-50"
                              data-testid={`complete-btn-${campaign.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Complete
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/whatsapp/campaigns/${campaign.id}/queue`)}
                            >
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                          </>
                        ) : campaign.status === 'draft' ? (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/whatsapp/campaigns/${campaign.id}/queue`)}
                              className="text-[#4F46E5] border-[#4F46E5]/20 hover:bg-[#4F46E5]/5"
                            >
                              <Play size={14} className="mr-1" />
                              Start
                            </Button>
                          </>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/whatsapp/campaigns/${campaign.id}/queue`)}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                        )}
                        
                        {campaign.status !== 'running' && (
                          <button 
                            onClick={() => handleDelete(campaign.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <Send className="mx-auto text-gray-300 mb-4" size={48} />
                    <h3 className="font-semibold text-gray-900 mb-2">No campaigns yet</h3>
                    <p className="text-gray-500 mb-4">Create your first campaign to start broadcasting</p>
                    <Button 
                      onClick={() => navigate('/whatsapp/campaigns/new')}
                      className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
                    >
                      <Plus size={16} className="mr-2" />
                      New Campaign
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default WACampaignsPage;
